/**
 * =============================================================================
 * GLOBAL DESIGN SYSTEM - AI-NATIVE INTERFACE
 * =============================================================================
 * 
 * This file defines the global design language for the entire application.
 * ALL components and pages MUST strictly inherit from these tokens.
 * 
 * PRINCIPLES:
 * - Action-driven, not report-driven
 * - Calm, modern AI-native aesthetic
 * - Semantic color usage only (no decorative colors)
 * - Clear visual hierarchy
 * - Operator clarity optimized for daily usage
 * 
 * DO NOT:
 * - Add decorative color use
 * - Increase visual density unnecessarily
 * - Use heavy gradients
 * - Add redundant UI elements
 */

// =============================================================================
// SPACING SYSTEM (8pt Grid)
// =============================================================================
/**
 * All spacing MUST use multiples of 8px.
 * Use these tokens for padding, margins, and gaps.
 */
export const spacing = {
    /** 4px - Micro spacing (icons, tight elements) */
    xxs: 4,
    /** 8px - Extra small (between inline elements) */
    xs: 8,
    /** 16px - Small (card internal padding, list items) */
    sm: 16,
    /** 20px - Medium-small (card padding) */
    md: 20,
    /** 24px - Medium (section gaps, card padding) */
    lg: 24,
    /** 32px - Large (section spacing, page padding) */
    xl: 32,
    /** 40px - Extra large (major sections) */
    xxl: 40,
    /** 48px - 2X large (page margins) */
    xxxl: 48,
} as const;

// =============================================================================
// TYPOGRAPHY HIERARCHY
// =============================================================================
/**
 * Strict typography scale. Use ONLY these sizes.
 * 
 * Font family: System default (Inter, -apple-system, etc.)
 * Font weights: 400 (regular), 500 (medium), 600 (semibold), 700 (bold)
 */
export const typography = {
    /** Page titles - 28px semibold */
    pageTitle: {
        fontSize: 28,
        fontWeight: 600,
        lineHeight: 1.2,
    },
    /** Section headers - 18px semibold */
    sectionHeader: {
        fontSize: 18,
        fontWeight: 600,
        lineHeight: 1.3,
    },
    /** Card titles - 16px semibold */
    cardTitle: {
        fontSize: 16,
        fontWeight: 600,
        lineHeight: 1.3,
    },
    /** Card subtitles / List item titles - 15px semibold */
    itemTitle: {
        fontSize: 15,
        fontWeight: 600,
        lineHeight: 1.3,
    },
    /** Body text - 14px regular */
    body: {
        fontSize: 14,
        fontWeight: 400,
        lineHeight: 1.5,
    },
    /** Secondary/muted text - 13px regular */
    secondary: {
        fontSize: 13,
        fontWeight: 400,
        lineHeight: 1.4,
    },
    /** Small text / captions - 12px regular */
    small: {
        fontSize: 12,
        fontWeight: 400,
        lineHeight: 1.4,
    },
    /** Labels / Tags - 10-11px medium uppercase */
    label: {
        fontSize: 10,
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: '0.05em',
        textTransform: 'uppercase' as const,
    },
    /** KPI / Large numbers - 34px bold */
    kpiValue: {
        fontSize: 34,
        fontWeight: 700,
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
    },
    /** KPI medium numbers - 24px bold */
    kpiMedium: {
        fontSize: 24,
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.01em',
    },
} as const;

// =============================================================================
// SEMANTIC COLORS
// =============================================================================
/**
 * STRICT semantic color usage. NO decorative colors allowed.
 * Each color has a specific meaning and use case.
 */
export const semanticColors = {
    /** Critical / Urgent - Use for errors, urgent alerts, destructive actions */
    critical: {
        primary: 'rgb(205, 101, 101)',      // --color-halt-800
        light: 'rgba(205, 101, 101, 0.1)',
        dark: 'rgb(164, 81, 81)',           // --color-halt-900
    },
    /** Warning / High priority - Use for warnings, high priority items */
    warning: {
        primary: 'rgb(219, 182, 88)',       // --color-mustard-800
        light: 'rgba(219, 182, 88, 0.1)',
        dark: 'rgb(175, 146, 70)',          // --color-mustard-900
    },
    /** Positive / Success - Use for success states, positive trends */
    positive: {
        primary: 'rgb(45, 187, 136)',       // --color-green
        light: 'rgba(45, 187, 136, 0.1)',
        dark: 'rgb(35, 150, 110)',
    },
    /** Informational / Primary - Use for links, primary actions, info states */
    info: {
        primary: 'rgb(1, 149, 255)',        // --color-blu-800
        light: 'rgba(1, 149, 255, 0.1)',
        dark: 'rgb(1, 119, 204)',           // --color-blu-900
    },
} as const;

// =============================================================================
// NEUTRAL COLORS
// =============================================================================
/**
 * Neutral palette for backgrounds, text, and borders.
 */
export const neutralColors = {
    /** Page background */
    background: '#FAFAFA',
    /** Card/surface background */
    surface: '#FFFFFF',
    /** Subtle background (hover, selected) */
    surfaceHover: 'rgba(0, 0, 0, 0.02)',
    /** Primary text - slate-900 */
    textPrimary: 'rgb(26, 32, 44)',
    /** Secondary text - slate-500 */
    textSecondary: 'rgb(100, 116, 139)',
    /** Muted text - slate-400 */
    textMuted: 'rgb(148, 163, 184)',
    /** Disabled text */
    textDisabled: 'rgb(189, 189, 189)',
    /** Border - subtle */
    border: 'rgba(0, 0, 0, 0.06)',
    /** Border - hover/focus */
    borderHover: 'rgba(0, 0, 0, 0.12)',
    /** Divider */
    divider: 'rgba(0, 0, 0, 0.04)',
} as const;

// =============================================================================
// ELEVATION / SHADOWS
// =============================================================================
/**
 * Shadow system for depth. Use sparingly.
 */
export const shadows = {
    /** No shadow */
    none: 'none',
    /** Subtle card shadow - default state */
    sm: '0 1px 3px rgba(0, 0, 0, 0.04)',
    /** Medium shadow - hover state */
    md: '0 4px 12px rgba(0, 0, 0, 0.08)',
    /** Large shadow - modals, dropdowns */
    lg: '0 8px 24px rgba(0, 0, 0, 0.12)',
    /** XL shadow - elevated modals */
    xl: '0 16px 48px rgba(0, 0, 0, 0.16)',
} as const;

// =============================================================================
// BORDER RADIUS
// =============================================================================
/**
 * Consistent border radius values.
 */
export const borderRadius = {
    /** Small - badges, pills */
    sm: 4,
    /** Medium - buttons, inputs */
    md: 8,
    /** Large - cards, containers */
    lg: 12,
    /** XL - modals, large cards */
    xl: 16,
    /** Full - circular elements */
    full: 9999,
} as const;

// =============================================================================
// SEVERITY INDICATOR
// =============================================================================
/**
 * Left-bar indicator for cards based on severity/priority.
 * Width: 4px, full height of card.
 */
export const severityIndicator = {
    width: 4,
    borderRadius: '12px 0 0 12px',
    colors: {
        critical: semanticColors.critical.primary,
        warning: semanticColors.warning.primary,
        positive: semanticColors.positive.primary,
        info: semanticColors.info.primary,
    },
} as const;

// =============================================================================
// PRIORITY MAPPING
// =============================================================================
/**
 * Map priority levels to semantic severity.
 */
export const priorityToSeverity = {
    urgent: 'critical',
    high: 'warning',
    medium: 'info',
    low: 'info',
} as const;

// =============================================================================
// CARD STYLES
// =============================================================================
/**
 * Standard card styling.
 */
export const cardStyles = {
    /** Default card */
    default: {
        backgroundColor: neutralColors.surface,
        borderRadius: borderRadius.lg,
        padding: spacing.md,
        boxShadow: shadows.sm,
    },
    /** Card with severity indicator */
    withIndicator: {
        backgroundColor: neutralColors.surface,
        borderRadius: borderRadius.lg,
        padding: spacing.md,
        paddingLeft: spacing.lg,
        boxShadow: shadows.sm,
    },
    /** Hover state */
    hover: {
        boxShadow: shadows.md,
        transform: 'translateY(-1px)',
    },
} as const;

// =============================================================================
// TRANSITIONS
// =============================================================================
/**
 * Standard transition values for animations.
 */
export const transitions = {
    /** Fast - micro interactions */
    fast: '0.1s ease',
    /** Default - most interactions */
    default: '0.2s ease',
    /** Slow - larger animations */
    slow: '0.3s ease',
} as const;

// =============================================================================
// Z-INDEX SCALE
// =============================================================================
/**
 * Z-index layering system.
 */
export const zIndex = {
    /** Base level */
    base: 0,
    /** Sticky elements */
    sticky: 100,
    /** Dropdowns, tooltips */
    dropdown: 200,
    /** Modals, dialogs */
    modal: 300,
    /** Toasts, notifications */
    toast: 400,
    /** Maximum (overlays) */
    max: 9999,
} as const;

// =============================================================================
// RESPONSIVE BREAKPOINTS
// =============================================================================
/**
 * Breakpoint values for responsive design.
 */
export const breakpoints = {
    /** Mobile */
    sm: 640,
    /** Tablet */
    md: 768,
    /** Desktop */
    lg: 1024,
    /** Large desktop */
    xl: 1280,
    /** Extra large */
    xxl: 1536,
} as const;

// =============================================================================
// COMPONENT-SPECIFIC TOKENS
// =============================================================================

/**
 * Page layout tokens
 */
export const pageLayout = {
    padding: {
        horizontal: spacing.xl,
        vertical: spacing.xl,
    },
    maxWidth: 1440,
    headerMarginBottom: spacing.xl,
    sectionGap: spacing.lg,
} as const;

/**
 * Table styling tokens
 */
export const tableStyles = {
    headerBackground: 'rgba(0, 0, 0, 0.02)',
    headerText: {
        ...typography.label,
        color: neutralColors.textSecondary,
    },
    rowPadding: `${spacing.sm}px ${spacing.md}px`,
    rowBorder: `1px solid ${neutralColors.divider}`,
    rowHoverBackground: neutralColors.surfaceHover,
} as const;

/**
 * Badge/pill styling tokens
 */
export const badgeStyles = {
    padding: `${spacing.xxs}px ${spacing.xs}px`,
    borderRadius: borderRadius.sm,
    fontSize: 10,
    fontWeight: 500,
} as const;

// =============================================================================
// CSS VARIABLE MAPPINGS
// =============================================================================
/**
 * Map design tokens to CSS custom properties.
 * Use these in global CSS for consistency.
 */
export const cssVariables = `
:root {
    /* Spacing */
    --spacing-xxs: ${spacing.xxs}px;
    --spacing-xs: ${spacing.xs}px;
    --spacing-sm: ${spacing.sm}px;
    --spacing-md: ${spacing.md}px;
    --spacing-lg: ${spacing.lg}px;
    --spacing-xl: ${spacing.xl}px;
    --spacing-xxl: ${spacing.xxl}px;
    
    /* Typography */
    --font-size-page-title: ${typography.pageTitle.fontSize}px;
    --font-size-section-header: ${typography.sectionHeader.fontSize}px;
    --font-size-card-title: ${typography.cardTitle.fontSize}px;
    --font-size-body: ${typography.body.fontSize}px;
    --font-size-small: ${typography.small.fontSize}px;
    --font-size-label: ${typography.label.fontSize}px;
    --font-size-kpi: ${typography.kpiValue.fontSize}px;
    
    /* Semantic Colors */
    --color-critical: ${semanticColors.critical.primary};
    --color-critical-light: ${semanticColors.critical.light};
    --color-warning: ${semanticColors.warning.primary};
    --color-warning-light: ${semanticColors.warning.light};
    --color-positive: ${semanticColors.positive.primary};
    --color-positive-light: ${semanticColors.positive.light};
    --color-info: ${semanticColors.info.primary};
    --color-info-light: ${semanticColors.info.light};
    
    /* Neutral Colors */
    --color-background: ${neutralColors.background};
    --color-surface: ${neutralColors.surface};
    --color-text-primary: ${neutralColors.textPrimary};
    --color-text-secondary: ${neutralColors.textSecondary};
    --color-text-muted: ${neutralColors.textMuted};
    --color-border: ${neutralColors.border};
    --color-divider: ${neutralColors.divider};
    
    /* Shadows */
    --shadow-sm: ${shadows.sm};
    --shadow-md: ${shadows.md};
    --shadow-lg: ${shadows.lg};
    
    /* Border Radius */
    --radius-sm: ${borderRadius.sm}px;
    --radius-md: ${borderRadius.md}px;
    --radius-lg: ${borderRadius.lg}px;
    
    /* Transitions */
    --transition-fast: ${transitions.fast};
    --transition-default: ${transitions.default};
}
`;

// =============================================================================
// TAILWIND CLASS HELPERS
// =============================================================================
/**
 * Common Tailwind class combinations for consistency.
 */
export const tailwindClasses = {
    /** Page title text */
    pageTitle: 'text-slate-900 font-semibold',
    /** Section header text */
    sectionHeader: 'text-slate-900 font-semibold',
    /** Card title text */
    cardTitle: 'text-slate-900 font-semibold',
    /** Secondary text */
    textSecondary: 'text-slate-500',
    /** Muted text */
    textMuted: 'text-slate-400',
    /** Label text */
    label: 'uppercase tracking-wider text-slate-400 font-semibold',
    /** Card container */
    card: 'bg-white rounded-xl shadow-sm',
    /** Card hover */
    cardHover: 'hover:shadow-md transition-all duration-200',
} as const;

// =============================================================================
// EXPORT ALL
// =============================================================================
export const designSystem = {
    spacing,
    typography,
    semanticColors,
    neutralColors,
    shadows,
    borderRadius,
    severityIndicator,
    priorityToSeverity,
    cardStyles,
    transitions,
    zIndex,
    breakpoints,
    pageLayout,
    tableStyles,
    badgeStyles,
    tailwindClasses,
} as const;

export default designSystem;
